(() => {
  'use strict';

  const RUNTIME_KEY = '__gdcGiftSizeSyncRuntime';
  if (window[RUNTIME_KEY]?.bound) return;

  const state = {
    bound: true,
    syncing: false,
    timer: null,
    cartUpdateUnsubscriber: null,
    lastTarget: null,
  };
  window[RUNTIME_KEY] = state;

  const sizeConfig = () => document.getElementById('GdcGiftSizeSyncConfig');
  const giftConfig = () => document.getElementById('GdcHalloweenGiftConfig');
  const root = () => window.Shopify?.routes?.root || '/';
  const endpoint = (path) => (root() + path).replace(/\\+/g, '/').replace('https:/', 'https://');

  const sVariantId = () => Number(sizeConfig()?.dataset?.sVariantId || 0);
  const lVariantId = () => Number(sizeConfig()?.dataset?.lVariantId || 0);
  const threshold = () => Number(giftConfig()?.dataset?.threshold || 88800);

  const normalizeSize = (value) =>
    String(value || '')
      .trim()
      .toUpperCase()
      .replace(/\bSIZE\b/g, '')
      .replace(/[._-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const sizeRank = (value) => {
    const token = normalizeSize(value);

    if (['XXL', '2XL', 'XX LARGE', 'DOUBLE XL'].includes(token)) return 6;
    if (['XL', 'X LARGE', 'EXTRA LARGE'].includes(token)) return 5;
    if (['L', 'LARGE'].includes(token)) return 4;
    if (['M', 'MEDIUM'].includes(token)) return 3;
    if (['S', 'SMALL'].includes(token)) return 2;
    if (['XS', 'X SMALL', 'EXTRA SMALL'].includes(token)) return 1;

    return 0;
  };

  const rankFromItem = (item) => {
    const namedOptions = Array.isArray(item?.options_with_values) ? item.options_with_values : [];

    for (const option of namedOptions) {
      const optionName = String(option?.name || '').trim().toLowerCase();
      const rank = sizeRank(option?.value);

      if ((optionName === 'size' || optionName.includes('size')) && rank > 0) return rank;
    }

    for (const option of namedOptions) {
      const rank = sizeRank(option?.value);
      if (rank > 0) return rank;
    }

    const variantOptions = Array.isArray(item?.variant_options) ? item.variant_options : [];
    for (const value of variantOptions) {
      const rank = sizeRank(value);
      if (rank > 0) return rank;
    }

    const titleCandidates = [item?.variant_title, item?.title];
    for (const value of titleCandidates) {
      const text = String(value || '').toUpperCase();
      const matches = text.match(/(?:^|[\s/|,-])(XXL|2XL|XL|XS|L|M|S)(?:\s*SIZE)?(?:$|[\s/|,-])/g) || [];
      for (const match of matches) {
        const rank = sizeRank(match);
        if (rank > 0) return rank;
      }
    }

    return 0;
  };

  const computeTarget = (cart, sId, lId) => {
    const giftIds = new Set([sId, lId]);
    let maxRank = 0;

    for (const item of cart?.items || []) {
      if (giftIds.has(Number(item?.variant_id || 0))) continue;
      maxRank = Math.max(maxRank, rankFromItem(item));
    }

    return {
      maxRank,
      targetId: maxRank >= 4 ? lId : sId,
      targetSize: maxRank >= 4 ? 'L' : 'S',
    };
  };

  const applyTarget = ({ targetId, targetSize, maxRank }) => {
    if (!targetId) return false;

    state.lastTarget = { targetId, targetSize, maxRank };

    const source = sizeConfig();
    const target = giftConfig();
    if (!source || !target) return false;

    const previous = Number(target.dataset.variantId || 0);

    source.dataset.targetVariantId = String(targetId);
    source.dataset.targetSize = targetSize;
    source.dataset.maxSizeRank = String(maxRank);
    target.dataset.variantId = String(targetId);

    return previous !== targetId;
  };

  const reapplyLastTarget = () => {
    if (!state.lastTarget) return false;
    return applyTarget(state.lastTarget);
  };

  const applyTargetFromCart = (cart) => {
    const sId = sVariantId();
    const lId = lVariantId();
    if (!sId || !lId || !cart?.items) return null;

    const target = computeTarget(cart, sId, lId);
    applyTarget(target);
    return target;
  };

  const fetchCart = async () => {
    const response = await fetch(endpoint('cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Cart read failed: ' + response.status);
    return response.json();
  };

  const changeLine = async (key, quantity) => {
    const response = await fetch(endpoint('cart/change.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: key,
        quantity,
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname,
      }),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload?.description || payload?.message || ('Cart update failed: ' + response.status));
    }
    return payload;
  };

  const renderDrawer = (payload) => {
    if (!payload?.sections) return;
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') drawer.renderContents(payload);
  };

  const onCartPage = () => {
    const cartPath = (root() + 'cart').replace(/\\+$/, '');
    return window.location.pathname.replace(/\\+$/, '') === cartPath;
  };

  const finishMutation = (payload) => {
    if (onCartPage()) {
      window.location.reload();
      return;
    }

    if (payload) renderDrawer(payload);

    // Cart drawer section rendering can restore the server-side default S
    // config. Restore the client-computed target synchronously before the
    // existing Halloween Gift module receives the cart-state event.
    reapplyLastTarget();
    document.dispatchEvent(new CustomEvent('gdc:cart-state-changed'));
  };

  const removeLines = async (lines) => {
    let lastPayload = null;
    for (const line of lines) {
      lastPayload = await changeLine(line.key, 0);
    }
    return lastPayload;
  };

  const sync = async () => {
    if (state.syncing) return;

    const sId = sVariantId();
    const lId = lVariantId();
    if (!sId || !lId) return;

    state.syncing = true;
    try {
      const cart = await fetchCart();
      const giftIds = new Set([sId, lId]);
      const giftLines = (cart?.items || []).filter((item) => giftIds.has(Number(item?.variant_id || 0)));
      const target = computeTarget(cart, sId, lId);
      const targetChanged = applyTarget(target);

      // Below the post-discount HK$888 threshold, neither gift size may remain.
      if (Number(cart?.total_price || 0) < threshold()) {
        if (giftLines.length) {
          finishMutation(await removeLines(giftLines));
        }
        return;
      }

      const wrongGiftLines = giftLines.filter(
        (item) => Number(item?.variant_id || 0) !== target.targetId
      );

      if (wrongGiftLines.length) {
        finishMutation(await removeLines(wrongGiftLines));
        return;
      }

      // If there is no gift yet and the client-side target changed, wake the
      // existing Halloween Gift module so it adds the correct S/L variant.
      if (!giftLines.length && targetChanged) {
        document.dispatchEvent(new CustomEvent('gdc:cart-state-changed'));
      }
    } catch (error) {
      console.warn('[GDC Gift Size Sync]', error);
    } finally {
      state.syncing = false;
    }
  };

  const schedule = (delay = 140) => {
    window.clearTimeout(state.timer);
    state.timer = window.setTimeout(sync, delay);
  };

  const onCartUpdate = (event) => {
    // Dawn publishes the updated cart object after quantity/remove changes.
    // Apply the size target immediately (before the existing gift subscriber
    // runs) so XL/L carts cannot fall back to the stale S config.
    if (event?.cartData?.items) {
      applyTargetFromCart(event.cartData);
    } else {
      reapplyLastTarget();
    }
    schedule(40);
  };

  const boot = () => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        state.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
      } catch (_) {}
    }

    schedule(0);
    window.addEventListener('pageshow', () => {
      reapplyLastTarget();
      schedule(0);
    });
    document.addEventListener('gdc:cart-state-changed', () => {
      reapplyLastTarget();
      schedule(60);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();